import { PlusOutlined, SortAscendingOutlined } from "@ant-design/icons";
import { Button, Divider } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../context/app/context";
import StorageUnitContext from "../../../context/wms/storage-unit/context";
import StorageUnitContextProvider from "../../../context/wms/storage-unit/provider";
import { useIsMount } from "../../../utils/hooks/is-mount";
import StorageUnitTable from "../../../components/wms/storage-unit/storage-unit-table";
import SearchFilterCard from "../../../components/general/filter-card";
import Can from "../../../utils/rbac/can";
import PageHeader from "../../../components/general/layout/page-header";
import RefreshBtn from "../../../components/general/table-components/actions/refresh-btn";
import { Permissions } from "../../../utils/Permissions/Permissions";
import StorageUnitFilter from "../../../components/wms/storage-unit/storage-unit-filter";
import styles from "./style.module.scss";

interface IProps {
  children?: React.ReactNode;
}

const StorageUnitComponent: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [sortEnabled, setSortEnabled] = useState(false);

  const { actions, loading, query } = useContext(StorageUnitContext);

  useEffect(() => {
    actions.setSearch(search);
  }, [search]);

  const isMounted = useIsMount();
  useEffect(() => {
    !isMounted && actions.getData();
  }, [query, direction]);

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("storage_units")}
        subTitle={t("storage_units_management")}
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("list")}
            onClick={() => actions.getData()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Can key={3} perform={Permissions.CAN_VIEW_ALL_WMS_RACKS}>
            <Divider type="vertical" />
            <Button
              type={sortEnabled ? "primary" : "default"}
              onClick={() => setSortEnabled(!sortEnabled)}
              icon={<SortAscendingOutlined />}
            >
              {sortEnabled ? t("disable_sorting") : t("enable_sorting")}
            </Button>
          </Can>,
          <Can key={2} perform={Permissions.CAN_VIEW_ALL_WMS_RACKS}>
            <Divider key={1} type="vertical" />
            <Button
              type="primary"
              onClick={() => {
                navigate(`/wms/storage-units/create`);
              }}
              icon={<PlusOutlined />}
            >
              {t("add_storage_unit")}
            </Button>
          </Can>,
        ]}
      />
      <SearchFilterCard
        formId="storage-unit-filter"
        searchValue={search}
        setSearchValue={setSearch}
        onReset={() => {
          actions.setQuery({
            page: query?.page,
            perPage: query?.perPage,
          });
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      >
        <StorageUnitFilter />
      </SearchFilterCard>

      <StorageUnitTable sortEnabled={sortEnabled} />
    </div>
  );
};

const StorageUnitPage = () => {
  return (
    <StorageUnitContextProvider>
      <StorageUnitComponent />
    </StorageUnitContextProvider>
  );
};

export default StorageUnitPage;
